import { useCallback, useEffect, useReducer, useRef } from 'react'
import { useApiClient } from './api-client'
import type { RuleViolation } from '../api/v1/client/services/rule-violations'

export interface UseRuleViolationsByTargetIdProps {
  targetId: string | undefined
  skip?: boolean
}

export interface UseRuleViolationsByTargetIdResult {
  data: RuleViolation[] | null
  loading: boolean
  error: Error | null
  refetch: () => Promise<RuleViolation[] | null>
}

interface State {
  data: RuleViolation[] | null
  loading: boolean
  error: Error | null
}

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: RuleViolation[] }
  | { type: 'FETCH_ERROR'; payload: Error }
  | { type: 'RESET' }

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null }
    case 'FETCH_SUCCESS':
      return { data: action.payload, loading: false, error: null }
    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.payload }
    case 'RESET':
      return { data: null, loading: false, error: null }
  }
}

const initialState = (active: boolean): State => ({
  data: null,
  loading: active,
  error: null,
})

/**
 * Retrieves rule violations for a single target id via the batched
 * `findByTargetIds` endpoint. Multiple concurrent hook instances coalesce into
 * one network request per ~50 ms window and share a 30 s per-id cache, so
 * rendering N components is cheaper than N independent requests.
 */
export const useRuleViolationsByTargetId = ({
  targetId,
  skip = false,
}: UseRuleViolationsByTargetIdProps): UseRuleViolationsByTargetIdResult => {
  const apiClient = useApiClient()
  const active = !skip && Boolean(targetId)
  const [state, dispatch] = useReducer(reducer, active, initialState)
  const latestRequestId = useRef(0)

  const fetchOne = useCallback(
    async (bypassCache = false): Promise<RuleViolation[] | null> => {
      if (!targetId) return null
      const requestId = ++latestRequestId.current
      dispatch({ type: 'FETCH_START' })
      try {
        if (bypassCache) {
          apiClient.ruleViolations.invalidateBatchCache(targetId)
        }
        const violations =
          await apiClient.ruleViolations.findByTargetIdsBatched(targetId)
        if (requestId === latestRequestId.current) {
          dispatch({ type: 'FETCH_SUCCESS', payload: violations })
        }
        return violations
      } catch (err) {
        if (requestId === latestRequestId.current) {
          dispatch({ type: 'FETCH_ERROR', payload: err as Error })
        }
        return null
      }
    },
    [apiClient, targetId],
  )

  const refetch = useCallback(() => fetchOne(true), [fetchOne])

  useEffect(() => {
    if (!active) {
      // Invalidate any in-flight request so its late response cannot update state.
      latestRequestId.current += 1
      dispatch({ type: 'RESET' })
      return
    }
    fetchOne()
  }, [active, fetchOne])

  return { ...state, refetch }
}
